import { type Editor } from "@tiptap/react";
import { type MouseEvent, type ReactNode, useRef, useCallback } from "react";
import { cx } from "shared/utils";
import type { AutoExpandConfig } from "./core/types/editor";

interface EditorContainerProps {
  autoExpandConfig?: AutoExpandConfig;
  children: ReactNode;
  editor: Editor;
  editorContainerClassName: string;
  id: string;
  isTouchDevice: boolean;
}

export const EditorContainer = (props: EditorContainerProps) => {
  const { autoExpandConfig, children, editor, editorContainerClassName, id } =
    props;

  const containerRef = useRef<HTMLDivElement>(null);

  // Set initial styles based on autoExpandConfig
  const getContainerStyle = useCallback((): React.CSSProperties => {
    if (!autoExpandConfig?.autoExpand) return {};

    return {
      height: autoExpandConfig.collapsedHeight
        ? `${autoExpandConfig.collapsedHeight}px`
        : "auto",
      width: autoExpandConfig.collapsedWidth
        ? `${autoExpandConfig.collapsedWidth}px`
        : "auto"
    };
  }, [autoExpandConfig]);

  const handleContainerClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (event.target !== event.currentTarget) return;
    if (!editor.isEditable) return;
    try {
      if (editor.isFocused) return; // If editor is already focused, do nothing

      editor.chain().focus("end", { scrollIntoView: false }).run(); // Focus the editor at the end
    } catch (error) {
      console.error(
        "An error occurred while handling container click to insert new empty node at bottom:",
        error
      );
    }
  };

  const handleContainerMouseLeave = () => {
    const dragHandleElement = document.querySelector("#editor-side-menu");
    if (!dragHandleElement?.classList.contains("side-menu-hidden"))
      dragHandleElement?.classList.add("side-menu-hidden");
  };

  return (
    <div
      ref={containerRef}
      id={`editor-container-${id}`}
      onClick={(event) => {
        handleContainerClick(
          event as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>
        );
      }}
      onMouseLeave={handleContainerMouseLeave}
      style={getContainerStyle()}
      className={cx(
        `editor-container cursor-text relative transition-all`,
        editor.isFocused && editor.isEditable ? "active-editor" : "",
        editorContainerClassName
      )}
    >
      {children}
    </div>
  );
};
